import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { CalendarDays, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api } from "@/lib/api";
import { errorText, formatDay, formatDateTime } from "@/lib/partnerApi";
import { PILL, StatusChip, ReviewUpdateDialog, notifyPartners } from "@/components/admin/partners/PartnerUpdatesAdmin";
import { PlannedDaysDialog } from "@/components/admin/partners/PartnersManager";

const DAY_STATE = {
    approved: ["Aprovada", "bg-emerald-100 text-emerald-800 border-emerald-200"],
    pending: ["Pendente", "bg-amber-100 text-amber-900 border-amber-200"],
    changes_requested: ["Em correção", "bg-orange-100 text-orange-900 border-orange-200"],
    rejected: ["Rejeitada", "bg-stone-200 text-stone-700 border-stone-300"],
    missing: ["Em falta", "bg-red-600 text-white border-red-600"],
    not_sent: ["Não enviada", "bg-white text-stone-700 border-stone-300"],
    planned: ["Prevista", "bg-stone-50 text-stone-500 border-stone-200 border-dashed"],
    justified: ["Justificada", "bg-sky-100 text-sky-800 border-sky-200"],
};

export function PartnerControl() {
    const [data, setData] = useState(null);
    const [review, setReview] = useState(null);
    const [justify, setJustify] = useState(null);
    const [days, setDays] = useState(null);

    const load = useCallback(() => {
        api.get("/admin/partner-control").then(({ data: d }) => setData(d)).catch(() => setData({ rows: [] }));
    }, []);
    useEffect(() => { load(); }, [load]);
    const changed = () => { load(); notifyPartners(); };

    const openDay = (row, d) => {
        if (d.update_id) setReview(d.update_id);
        else if (d.state === "missing") setJustify({ row, day: d.date, rejectedId: d.rejected_update_id });
    };

    if (!data) return <Loader2 className="w-5 h-5 animate-spin text-stone-400" />;
    return (
        <div data-testid="admin-partner-control">
            <Legend />
            {data.rows.length === 0 ? (
                <p className="text-sm text-stone-400 italic" data-testid="partner-control-empty">Sem obras atribuídas a parceiros.</p>
            ) : (
                <ul className="space-y-3">
                    {data.rows.map((r) => (
                        <li key={r.assignment_id} className="bg-white rounded-lg border border-stone-200 p-4" data-testid={`control-row-${r.assignment_id}`}>
                            <div className="flex flex-wrap items-start justify-between gap-3">
                                <div className="min-w-0">
                                    <div className="font-semibold text-stone-900">{r.obra_title}</div>
                                    <div className="text-xs text-stone-500">{r.partner_name}{!r.partner_active && " (inativo)"} · Cliente: {r.lead_name}</div>
                                </div>
                                {r.missing > 0
                                    ? <span className={`${PILL} bg-red-600 text-white`} data-testid={`control-missing-${r.assignment_id}`}>{r.missing} em falta</span>
                                    : <span className={`${PILL} bg-emerald-100 text-emerald-800`} data-testid={`control-ok-${r.assignment_id}`}>Em dia</span>}
                            </div>
                            <div className="mt-2 text-xs text-stone-600 flex flex-wrap gap-x-4 gap-y-1">
                                <span>Última atualização: {r.last_update ? <><strong className="capitalize">{formatDay(r.last_update.work_date)}</strong> <StatusChip status={r.last_update.status} /></> : "nenhuma"}</span>
                                {r.next_planned && <span>Próximo dia previsto: <strong className="capitalize">{formatDay(r.next_planned)}</strong></span>}
                            </div>
                            <div className="mt-3 flex gap-1.5 overflow-x-auto pb-1" data-testid={`control-days-${r.assignment_id}`}>
                                {r.days.length === 0 && <span className="text-xs text-stone-400 italic">Sem dias de intervenção marcados.</span>}
                                {r.days.map((d) => <DayChip key={d.date} d={d} onClick={() => openDay(r, d)} testid={`control-day-${r.assignment_id}-${d.date}`} />)}
                            </div>
                            <button type="button" onClick={() => setDays(r.assignment_id)} className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold text-[hsl(var(--copper))]" data-testid={`control-edit-days-${r.assignment_id}`}>
                                <CalendarDays className="w-3.5 h-3.5" /> Dias previstos
                            </button>
                        </li>
                    ))}
                </ul>
            )}
            <ReviewUpdateDialog id={review} onClose={() => setReview(null)} onChanged={changed} />
            <JustifyDialog target={justify} onClose={() => setJustify(null)} onSaved={() => { setJustify(null); changed(); }}
                onReview={(id) => { setJustify(null); setReview(id); }} />
            {days && <PlannedDaysDialog assignmentId={days} onClose={() => setDays(null)} onSaved={changed} />}
        </div>
    );
}

function DayChip({ d, onClick, testid }) {
    const [label, cls] = DAY_STATE[d.state] || DAY_STATE.planned;
    const clickable = d.update_id || d.state === "missing";
    const source = d.planned ? "Previsto pelo administrador" : d.worked ? "Acrescentado pelo parceiro" : "Atualização enviada";
    return (
        <button type="button" onClick={onClick} disabled={!clickable} title={`${source}${d.rejected_update_id ? " · a única atualização foi rejeitada" : ""}${d.reason ? ` · ${d.reason}` : ""}${d.late ? " · enviada com atraso" : ""}`}
            className={`flex-shrink-0 rounded-lg border px-2.5 py-1.5 text-left ${cls} ${clickable ? "cursor-pointer hover:opacity-85" : "cursor-default"}`} data-testid={testid}>
            <div className="text-[11px] font-semibold capitalize">{formatDay(d.date, { weekday: "short", day: "2-digit", month: "2-digit" })}{!d.planned && d.worked && " · P"}</div>
            <div className="text-[10px]">{label}{d.rejected_update_id && " · rejeitada"}{d.late && " · atraso"}</div>
        </button>
    );
}

function Legend() {
    return (
        <div className="mb-4 rounded-lg bg-white border border-stone-200 p-3 text-[11px] text-stone-600" data-testid="partner-control-legend">
            <div className="flex flex-wrap gap-1.5 mb-1.5">
                {Object.entries(DAY_STATE).map(([key, [label, cls]]) => <span key={key} className={`rounded border px-2 py-0.5 ${cls}`}>{label}</span>)}
            </div>
            Só conta como "Em falta" um dia de intervenção (previsto, ou acrescentado pelo parceiro — marcado "P") já terminado, sem atualização válida nem justificação. Uma atualização enviada nunca está em falta, mesmo que ainda aguarde validação; se a única atualização do dia foi rejeitada, o dia fica "Em falta" (a atualização mantém o estado "Rejeitada" no histórico). Clique num dia em falta para justificar, ou num dia com envio para o rever.
        </div>
    );
}

function JustifyDialog({ target, onClose, onSaved, onReview }) {
    const [reason, setReason] = useState("");
    const [busy, setBusy] = useState(false);
    useEffect(() => { setReason(""); }, [target]);

    const save = async () => {
        setBusy(true);
        try {
            await api.post(`/admin/partner-assignments/${target.row.assignment_id}/exceptions`, { date: target.day, reason });
            toast.success("Justificação registada.");
            onSaved();
        } catch (e) {
            toast.error(errorText(e));
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog open={!!target} onOpenChange={(o) => { if (!o) onClose(); }}>
            <DialogContent className="max-w-md" data-testid="justify-dialog">
                <DialogHeader><DialogTitle>Justificar dia em falta</DialogTitle></DialogHeader>
                {target && (
                    <div className="space-y-3">
                        <p className="text-sm text-stone-600">{target.row.partner_name} · <span className="capitalize">{formatDay(target.day)}</span></p>
                        {target.rejectedId && (
                            <button type="button" onClick={() => onReview(target.rejectedId)} className="text-xs font-semibold text-[hsl(var(--copper))] underline" data-testid="justify-view-rejected">
                                A única atualização deste dia foi rejeitada — ver atualização
                            </button>
                        )}
                        <textarea rows={3} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Ex.: Não houve intervenção por falta de material." className="w-full px-3 py-2 border border-stone-300 rounded text-sm" data-testid="justify-reason" />
                        <p className="text-[11px] text-stone-500">Fica registado no histórico ({formatDateTime(new Date().toISOString())}).</p>
                        <button type="button" onClick={save} disabled={!reason.trim() || busy} className="px-4 py-2.5 rounded-full bg-[hsl(var(--forest))] text-white text-xs font-semibold uppercase tracking-wider disabled:opacity-40" data-testid="justify-save">
                            {busy && <Loader2 className="w-3.5 h-3.5 animate-spin inline mr-1" />}Guardar justificação
                        </button>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}
